import { ReactNode, forwardRef, useEffect, useMemo, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { BsSearch, BsX } from 'react-icons/bs';

const CustomToggle = forwardRef<
  HTMLElement,
  { children: ReactNode; onClick: (event: MouseEvent) => void }
>(({ children, onClick }, ref) => (
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
  <div
    className="position-relative"
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e as unknown as MouseEvent);
    }}
  >
    <BsSearch size={16} className="autosuggest-search-icon" />
    {children}
  </div>
));

CustomToggle.displayName = 'CustomToggle';

export const AutoSuggest = ({
  options = [],
  value,
  onChange,
  placeholder,
}: {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => {
  const [inputValue, setInputValue] = useState<string>('');

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const show = useMemo(
    () =>
      options?.filter((option) => option.toLowerCase().includes(inputValue.toLocaleLowerCase()))
        .length > 0 && inputValue.length >= 1,
    [inputValue, options]
  );

  const onChangeHandler = (newValue: string) => {
    onChange(newValue);
    setInputValue(newValue);
  };

  const displayedOptions = useMemo(() => {
    return options.filter((option) =>
      option.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase())
    );
  }, [inputValue, options]);

  const getHighlightedText = (text) => {
    const parts = text.split(new RegExp(`(${inputValue})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) => (
          <span
            key={i}
            style={part.toLowerCase() === inputValue.toLowerCase() ? { fontWeight: 'bold' } : {}}
          >
            {part}
          </span>
        ))}
      </span>
    );
  };

  return (
    <>
      <Dropdown onSelect={(value) => onChangeHandler(value ?? '')} className="autosuggest">
        <Dropdown.Toggle as={CustomToggle}>
          <Form.Control
            className="autosuggest-input"
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onInput={(e) => onChangeHandler(e.target.value)}
          />
          {inputValue && (
            <BsX
              size={26}
              viewBox={'0 0 15 15'}
              className="autosuggest-close-icon"
              onClick={() => onChangeHandler('')}
            />
          )}
        </Dropdown.Toggle>
        {show && (
          <Dropdown.Menu rootCloseEvent="mousedown" className="autosuggest-dropdown">
            {displayedOptions.map((option, index) => (
              <Dropdown.Item key={index} eventKey={option}>
                {getHighlightedText(option)}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        )}
      </Dropdown>
    </>
  );
};
